import { useMemo } from 'react';
import { getWidgetPath } from '../../../../../../validator/form-builder/getWidgetPath.ts';
import { useBooleanCondition } from '../../../../hooks/useBooleanCondition.tsx';
import { type IWidgetOptionValue, WidgetType } from '../../../../Widget.ts';
import { type IMultiSelectWidget } from '../../../inputs/multi-select/IMultiSelectWidget.ts';
import { type ISingleSelectWidget } from '../../../inputs/single-select/ISingleSelectWidget.ts';
import { WidgetRepository } from '../../../WidgetRepository.ts';
import { type ITableInputWidget } from '../ITableInputWidget.ts';
import { type ITableInputWidgetColumnsContext } from './useTableInputWidgetColumns.tsx';

export interface ITableColumnOptionComponentProps {
  context: ITableInputWidgetColumnsContext;
  rowIndex: number;
  optionIndex: number;
  index: number;
  column: IColumnGroupableWidget;
  /**
   * Siamo sicuri sia un `IColumnGroupableWidget` perché, in caso di riga custom,
   * siamo già "usciti" da questo componente, vedi `shouldRenderCustomCell`.
   */
  cellWidget: IColumnGroupableWidget;
  option: IWidgetOptionValue;
  table: ITableInputWidget;
}

export type IColumnGroupableWidget = IMultiSelectWidget | ISingleSelectWidget;

/**
 * Componente che gestisce il render delle singole opzioni nelle colonne
 * di una tabella.
 * Le opzioni (in formato radio/checkbox) sono mostrate 1 per sottocolonna,
 * all'interno di un columngroup che le riunisce tutte insieme.
 */
export function TableColumnOptionComponent(
  props: ITableColumnOptionComponentProps,
) {
  const { column, option, context, cellWidget } = props;

  const cellOption = useMemo(() => {
    // Prendiamo l'opzione _materializzata_ che ha le reference
    // virtuali già sostituite
    return cellWidget.options?.find(
      o => o.type === 'option' && o.value === option.value,
    ) as IWidgetOptionValue | null;
  }, [cellWidget, option.value]);

  const component = useMemo(
    () => WidgetRepository.find(column.type),
    [column.type],
  );

  const name = useMemo(() => {
    return getWidgetPath(cellWidget, context.prefix);
  }, [cellWidget, context.prefix]);

  const visible = useBooleanCondition(cellOption?.visible, {
    name,
    preview: context.options.preview,
    // TODO Saltiamo perché sulle multi la condizione è diversa (vedi `remap`) ed è già gestita nel CheckboxOptionInput
    skip: cellWidget.type === WidgetType.MultiSelect,
  });

  const enabled = useBooleanCondition(cellOption?.enabled, {
    name,
    preview: context.options.preview,
    skip: cellWidget.type === WidgetType.MultiSelect,
  });

  const optionWithFlags = useMemo(() => {
    return { ...option, disabled: enabled === false };
  }, [enabled, option]);

  // Aggiungiamo un messaggio di warning se non è stato trovato il componente.
  // Non dovrebbe mai capitare, si potrebbe rendere più robusto il codice
  // in `useTableInputWidgetColumns` per allineare i Typing riconoscendo
  // questa casistica a compile-time
  if (!component?.optionComponent) {
    // biome-ignore format: <tbd>
    console.warn(`[TableColumnOptionComponent] No optionComponent for ${name} (cellWidget ${cellWidget.type} id=${cellWidget.id})`);
    return null;
  }

  if (!visible) return null;

  return (
    <component.optionComponent
      readonly={context.options.readonly}
      widget={cellWidget}
      option={optionWithFlags}
      hideLabel
      name="option"
      // TODO Capire come gestire, attualmente non va bene se si hanno campi annidati
      groupName={name}
    />
  );
}
